import { useRef, useState } from 'react'
import {
  bookingModeOf, canBook, canSelfCancel, formatEventMeta, iconShare, isPastEvent,
  type EventRecord
} from '@romain130492/topchopsticks'
import { useI18n } from '../hooks/useI18n'
import AttendeeAvatars from './AttendeeAvatars'
import EventDetailsBody from './EventDetailsBody'
import Button from './Button'

/**
 * One event. "See details" expands the FULL detail body — the very same
 * component the standalone page renders, so nothing is visible there and
 * missing here.
 */
export default function EventCard({
  event, onReserve, onCancel, onOpen, onShare, booked = false, busy = false, seatsReady = true,
  forceOpen = false, attendeesKey = 0, registered = false, onRegister
}: {
  event: EventRecord
  onReserve?: (event: EventRecord) => void
  onCancel?: (event: EventRecord) => void
  onOpen?: (event: EventRecord) => void
  /** False while /me/event-matches is still in flight. Disables, never spins. */
  seatsReady?: boolean
  booked?: boolean
  busy?: boolean
  forceOpen?: boolean
  attendeesKey?: number
  registered?: boolean
  onRegister?: (reason?: string) => void
  onShare?: (event: EventRecord) => void
}) {
  const { t, L, lang } = useI18n()
  const [open, setOpen] = useState(false)

  /**
   * Hidden QA shortcut, same as the mini program: triple-tap the card BODY
   * (never the buttons) to open the standalone event page. Taps must land
   * within 550ms of each other.
   */
  const tapRef = useRef({ count: 0, lastAt: 0 })
  const handleSecretOpenTap = () => {
    if (!onOpen) return
    const now = Date.now()
    const count = now - tapRef.current.lastAt <= 550 ? tapRef.current.count + 1 : 1
    tapRef.current = { count, lastAt: now }
    if (count < 3) return
    tapRef.current = { count: 0, lastAt: 0 }
    onOpen(event)
  }
  const isOpen = forceOpen || open

  const mode = bookingModeOf(event)
  const bookable = canBook(event)
  const cancellable = booked && bookable && canSelfCancel(mode)
  const past = isPastEvent(event)
  const cover = event.cover_image_url || event.pictures?.[0]?.sizes?.original || ''
  const seatsRemaining = Number.isFinite(Number(event.seats_remaining)) ? Number(event.seats_remaining) : null

  const primaryLabel = (() => {
    if (past) return booked ? t('ev.attendedLabel') : t('ev.finishedLabel')
    if (!registered) return t('guest.cta')
    if (booked) return cancellable ? t('ev.cancelReservation') : t('ev.bookedLabel')
    if (mode === 'free') return `${t('ev.join')} · ${t('ev.free')}`
    if (mode === 'pay_later') return t('ev.reserveNoPay')
    return t('ev.reserve')
  })()

  return (
    <article className="mb-[22px] overflow-hidden rounded-md border border-gold-dim bg-card">
      <div onClick={handleSecretOpenTap}>
        {cover ? <img src={cover} alt="" className="h-44 w-full object-cover" /> : null}
      </div>

      {/* .card-pad — 16px vertical, 18px horizontal. */}
      <div className="px-[18px] py-4" onClick={handleSecretOpenTap}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-h2 leading-snug text-ivory">{L(event.title as never)}</h2>
          {/* A pending table is not in `events`, so a shared link resolves for
              nobody — no share affordance until it is real. */}
          {event.is_request ? null : (
            <button
              type="button"
              aria-label="Share"
              onClick={(clickEvent) => { clickEvent.stopPropagation(); onShare && onShare(event) }}
              className="flex h-6 w-6 shrink-0 items-center justify-center"
            >
              {/* The mini program's icon, from the shared package. */}
              <img src={iconShare} alt="" className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>

        <p className="mt-1 text-sm text-muted">{formatEventMeta(event, lang)}</p>
        <AttendeeAvatars eventId={event.id} refreshKey={attendeesKey} anonymous={!registered} />

        {seatsRemaining !== null ? (
          <p className="mt-2 text-sm text-muted">{t('ev.seatsRemaining')}: {seatsRemaining}</p>
        ) : null}

        {isOpen ? <EventDetailsBody event={event} /> : null}

        {/* .btn-row — side by side, not stacked: flex, gap 8px, each flex-1,
            min-height 42px, 13px text, 0.04em tracking. */}
        <div className="mt-3.5 flex gap-2">
          {!forceOpen ? (
            // Same component as its neighbour: two buttons sitting side by
            // side must not be built two different ways, or they drift.
            // `.btn.ghost` is gold-on-transparent with a gold-dim border.
            <Button
              size="row"
              variant="ghost"
              className="border-[rgba(201,169,110,0.5)] text-gold"
              onClick={() => setOpen((o) => !o)}
            >
              {isOpen ? t('ev.hide') : t('ev.details')}
            </Button>
          ) : null}

          {/* No handler and not a guest = nothing to book (a past table, or a
              pending request). A dead button there would be a lie. */}
          {onReserve || onCancel || !registered ? (
            <Button
              size="row"
              variant={cancellable ? 'danger' : 'primary'}
              loading={busy}
              // SPINNER ONLY FOR A REAL ACTION.
              //
              // `busy` used to include `!booking.ready`, so every Join button
              // span the moment the page loaded and kept spinning until
              // /me/event-matches came back — a spinner that means "I am
              // fetching something unrelated" reads as "your tap is being
              // processed", which is a lie the user then waits on.
              //
              // Not knowing whether a seat is already held is still a reason
              // not to accept a tap (it could double-book), so it disables.
              // Disabled says "not yet"; a spinner says "working on it".
              disabled={registered && (!seatsReady || !bookable || (booked && !cancellable))}
              onClick={() => {
                if (!registered) return onRegister && onRegister(t('guest.reserveLocked'))
                return cancellable ? onCancel && onCancel(event) : onReserve && onReserve(event)
              }}
            >
              {primaryLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  )
}
